import {
  CSRF_HEADER,
  changeRequestState,
  isFolderOp,
  type ProjectDetail,
  type ProjectView,
  trustState,
  type WikiPageView,
  wikiSlug,
} from "@esi/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Check,
  ExternalLink,
  FileText,
  Folder,
  MessageSquare,
  Send,
  X,
} from "lucide-react";
import { type MouseEvent, type Ref, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppDialog } from "@/components/AppDialog";
import { Markdown } from "@/components/markdown/Markdown";
import { Notice } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { api } from "../api.ts";
import { noContent, ok } from "../api-errors.ts";
import { IconButton } from "../conversation/artefacts/ArtefactPanel.tsx";
import { formatDateTime } from "../i18n/format.ts";
import { can, canChange } from "../projects/capabilities.ts";
import { useProject } from "../projects/queries.ts";
import { changeSetPath, Link, type OpenWiki, wikiPath } from "../router.tsx";
import { useSession } from "../session.tsx";
import { AuthorPreview, authorPreviewOf } from "./AuthorPreview.tsx";
import { ChangeSetPublishDialog } from "./ChangeSetPublishDialog.tsx";
import { ItemContent, itemFacts, itemName } from "./ChangeSetReview.tsx";
import type { ReadingView } from "./MarkedPage.tsx";
import { FolderNeighbours, OnThisPage, PageBreadcrumb } from "./PageNavigation.tsx";
import { useHeadingAside, usePageThreads } from "./PageThreads.tsx";
import { useChangeItem, useChangeSet, useWikiPage, wikiKeys } from "./queries.ts";
import { ThreadPanel } from "./ThreadPanel.tsx";

/** A wiki page's ID in a link the reading renderer wrote (`/projects/:p/wiki/:page`). */
function pageOfHref(
  href: string,
  projectId: string,
): { pageId: string; version: number | null } | null {
  const url = new URL(href, window.location.origin);
  const match = new RegExp(`^/projects/${projectId}/wiki/([0-9a-f-]{36})$`, "i").exec(url.pathname);
  if (!match?.[1]) {
    return null;
  }
  const v = Number(url.searchParams.get("version"));
  return { pageId: match[1].toLowerCase(), version: Number.isInteger(v) && v > 0 ? v : null };
}

/**
 * The wiki side panel of a project conversation (wiki-collaboration D8a): a readable page, or a
 * change set with its items and the selected item's diff, beside the thread in the column
 * artefacts use. It reads only the page and change-set routes, as the wiki view and the review
 * do (a hidden page is the wiki view's not-found); nothing is kept in the browser beyond the URL.
 * Wiki links inside a page open in the panel, with Back to the page before.
 */
export function WikiPanel(props: {
  projectId: string;
  open: OpenWiki;
  headingRef: Ref<HTMLHeadingElement>;
  onNavigate: (next: OpenWiki) => void;
  onBack: (() => void) | undefined;
  /**
   * "← Knowledge" (D10): back to the Knowledge panel as it was, when the conversation has one and
   * there is no page to go back to.
   */
  onKnowledge?: (() => void) | undefined;
  onClose: () => void;
  /** In the narrow screens' sheet: the sheet has its own title and close control. */
  inSheet: boolean;
}) {
  const { projectId, open, headingRef, onNavigate, onBack, onKnowledge, onClose, inSheet } = props;
  const { t } = useTranslation();
  const project = useProject(projectId).data;
  const { userId } = useSession();
  // The creator's own open set reads as their preview (wiki-collaboration-ux): its full page too.
  const changes = useChangeSet(
    projectId,
    open.kind === "changes" ? open.changeSetId : "",
    open.kind === "changes",
  );
  const ownDraft = !!changes.data && authorPreviewOf(changes.data, userId);
  return (
    <div data-esi-content="wiki" className="flex min-h-0 flex-1 flex-col">
      <div className="flex h-bar shrink-0 items-center justify-between gap-2 border-b border-line px-2">
        {onBack ? (
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ArrowLeft aria-hidden="true" strokeWidth={1.75} />
            {t("wiki.panel.back")}
          </Button>
        ) : onKnowledge ? (
          <Button variant="ghost" size="sm" onClick={onKnowledge}>
            <ArrowLeft aria-hidden="true" strokeWidth={1.75} />
            {t("wiki.panel.knowledge")}
          </Button>
        ) : (
          <span />
        )}
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" asChild>
            <Link
              href={
                open.kind === "page"
                  ? wikiPath(projectId, open.pageId, open.version ?? undefined)
                  : changeSetPath(projectId, open.changeSetId, open.itemId ?? undefined)
              }
            >
              <ExternalLink aria-hidden="true" strokeWidth={1.75} />
              {open.kind === "page"
                ? t("wiki.panel.openInWiki")
                : ownDraft
                  ? t("wiki.author.openFull")
                  : t("wiki.panel.openReview")}
            </Link>
          </Button>
          {inSheet ? null : (
            <IconButton label={t("wiki.panel.close")} onClick={onClose}>
              <X aria-hidden="true" strokeWidth={1.75} />
            </IconButton>
          )}
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {open.kind === "page" ? (
          <PanelPage
            projectId={projectId}
            project={project}
            pageId={open.pageId}
            version={open.version}
            headingRef={headingRef}
            onNavigate={onNavigate}
          />
        ) : project ? (
          <PanelChanges
            project={project}
            id={open.changeSetId}
            itemId={open.itemId}
            runId={open.runId ?? null}
            headingRef={headingRef}
            onNavigate={onNavigate}
          />
        ) : (
          <Skeleton className="m-4 h-32" />
        )}
      </div>
    </div>
  );
}

/**
 * The page's discussion stays on the full page (D10, Q14): the panel shows its open threads'
 * count as a link there, at the version shown. Nothing for a guest or on an N-1 API.
 */
function PanelDiscussionLink(props: {
  project: Pick<ProjectView, "id" | "capabilities" | "frozenAt">;
  projectId: string;
  page: WikiPageView;
}) {
  const { t } = useTranslation();
  const threads = usePageThreads(props.project, props.page.id);
  if (!can(props.project, "wiki.comment") || threads.isError || !threads.data) {
    return null;
  }
  const open = threads.data.filter((th) => th.status === "open").length;
  return (
    <p className="flex items-center gap-1.5 border-t border-line pt-4 text-[13px]">
      <MessageSquare aria-hidden="true" className="size-4 text-fg-muted" strokeWidth={1.75} />
      <Link
        href={wikiPath(
          props.projectId,
          props.page.id,
          props.page.shownVersion === props.page.version ? undefined : props.page.shownVersion,
        )}
        data-leave-panel=""
        className="text-accent-text underline-offset-4 hover:underline"
      >
        {open ? t("wiki.panel.discussion", { count: open }) : t("wiki.panel.discussionNone")}
      </Link>
    </p>
  );
}

const NO_PROJECT: Pick<ProjectView, "capabilities" | "frozenAt"> = {
  capabilities: [],
  frozenAt: null,
};

function PanelPage(props: {
  projectId: string;
  /** For the page's discussion (D17); none until the project loads. */
  project: Pick<ProjectView, "id" | "capabilities" | "frozenAt"> | undefined;
  pageId: string;
  version: number | null;
  headingRef: Ref<HTMLHeadingElement>;
  onNavigate: (next: OpenWiki) => void;
}) {
  const { projectId, pageId, version, headingRef, onNavigate } = props;
  const { t } = useTranslation();
  const page = useWikiPage(projectId, pageId, version ?? undefined);
  const discussion = props.project ?? { id: projectId, ...NO_PROJECT };
  const aside = useHeadingAside(
    discussion,
    {
      id: pageId,
      markdown: page.data?.markdown ?? "",
      version: page.data?.version ?? 0,
      shownVersion: page.data?.shownVersion ?? -1,
    },
    "wiki-panel-section",
  );
  if (page.isError) {
    return (
      <div className="flex flex-col gap-3 p-4">
        <h2 ref={headingRef} tabIndex={-1} className="sr-only">
          {t("wiki.panel.notFound")}
        </h2>
        <Notice tone="danger">{page.error.message}</Notice>
      </div>
    );
  }
  if (!page.data) {
    return <Skeleton className="m-4 h-48" />;
  }
  const p = page.data;
  const byTitle = new Map(
    p.links.flatMap((l) => (l.page ? [[wikiSlug(l.title), l.page.id] as const] : [])),
  );
  const shown = p.versions.find((v) => v.version === p.shownVersion);
  // Wiki links and backlinks open in the panel; any other link behaves as usual.
  const follow = (event: MouseEvent<HTMLElement>) => {
    const anchor = (event.target as HTMLElement).closest("a[href]");
    // A link marked to leave the panel (the discussion on the full page) navigates as usual.
    const target =
      anchor && !anchor.hasAttribute("data-leave-panel")
        ? pageOfHref(anchor.getAttribute("href") ?? "", projectId)
        : null;
    if (
      !target ||
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey
    ) {
      return;
    }
    event.preventDefault();
    onNavigate({ kind: "page", pageId: target.pageId, version: target.version });
  };
  return (
    <article className="flex flex-col gap-5 px-4 py-4 sm:px-5" onClickCapture={follow}>
      <header className="flex flex-col gap-1.5">
        <PageBreadcrumb
          projectId={projectId}
          page={p}
          onOpen={(id) => onNavigate({ kind: "page", pageId: id, version: null })}
        />
        <h2
          ref={headingRef}
          tabIndex={-1}
          className="text-lg font-semibold tracking-tight text-balance break-words text-fg outline-none"
        >
          {p.title}
        </h2>
        <p className="text-xs text-fg-muted tabular-nums">
          {t(`wiki.pageTypes.${p.pageType}`)} ·{" "}
          {t("wiki.panel.versionLine", {
            version: p.shownVersion,
            date: formatDateTime(shown?.publishedAt ?? p.updatedAt),
          })}
        </p>
        {p.newerVersion ? (
          <Notice
            tone="info"
            action={
              <Button
                size="xs"
                variant="outline"
                onClick={() => onNavigate({ kind: "page", pageId: p.id, version: null })}
              >
                {t("wiki.panel.showCurrent")}
              </Button>
            }
          >
            {t("wiki.newerVersion")}
          </Notice>
        ) : null}
      </header>
      <OnThisPage markdown={p.markdown} title={p.title} prefix="wiki-panel-section" />
      <Markdown
        headingPrefix="wiki-panel-section"
        omitTitleHeading={p.title}
        markdown={p.markdown}
        headingAside={aside.render}
        resolve={(title) => {
          const id = byTitle.get(wikiSlug(title));
          return id ? wikiPath(projectId, id) : null;
        }}
      />
      {aside.dialog}
      {props.project ? (
        <PanelDiscussionLink project={props.project} page={p} projectId={projectId} />
      ) : null}
      <FolderNeighbours
        projectId={projectId}
        pageId={p.id}
        onOpen={(id) => onNavigate({ kind: "page", pageId: id, version: null })}
      />
      {p.backlinks.length ? (
        <section className="flex flex-col gap-2 border-t border-line pt-4">
          <h3 className="text-xs font-medium text-fg-muted">{t("wiki.panel.linkedFrom")}</h3>
          <ul className="flex flex-col gap-1 text-sm">
            {p.backlinks.map((b) => (
              <li key={b.id}>
                <Link
                  href={wikiPath(projectId, b.id)}
                  className="text-accent-text underline-offset-4 hover:underline"
                >
                  {b.title}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {p.versions.length > 1 ? (
        <section className="flex flex-col gap-2 border-t border-line pt-4">
          <h3 className="text-xs font-medium text-fg-muted">{t("wiki.panel.history")}</h3>
          <ul className="divide-y divide-line rounded-lg border border-line bg-surface-raised">
            {p.versions.map((v) => (
              <li key={v.version}>
                <button
                  type="button"
                  aria-current={v.version === p.shownVersion ? "true" : undefined}
                  className={cn(
                    "flex w-full flex-col gap-0.5 px-3 py-2 text-left text-xs hover:bg-surface-hover",
                    v.version === p.shownVersion && "bg-surface-selected",
                  )}
                  onClick={() =>
                    onNavigate({
                      kind: "page",
                      pageId: p.id,
                      version: v.version === p.version ? null : v.version,
                    })
                  }
                >
                  <span className="font-medium text-fg-secondary tabular-nums">
                    {t("wiki.version", { version: v.version })}
                  </span>
                  <span className="text-fg-secondary tabular-nums">
                    {formatDateTime(v.publishedAt)}
                    {v.authorName ? ` · ${v.authorName}` : ""}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </article>
  );
}

function PanelChanges(props: {
  project: ProjectDetail;
  id: string;
  itemId: string | null;
  /** A resolution run's replies to validate (wiki-resolve-with-agent D4). */
  runId: string | null;
  headingRef: Ref<HTMLHeadingElement>;
  onNavigate: (next: OpenWiki) => void;
}) {
  const { project, id, itemId, runId, headingRef, onNavigate } = props;
  const { t } = useTranslation();
  const { csrfToken, userId } = useSession();
  const queryClient = useQueryClient();
  const set = useChangeSet(project.id, id);
  const selected = set.data?.items.find((i) => i.id === itemId) ?? set.data?.items[0];
  const item = useChangeItem(project.id, id, selected?.id);
  const [dialog, setDialog] = useState<"publish" | "discard" | null>(null);
  const [view, setView] = useState<ReadingView>("marked");
  const endpoint = api.api.projects[":projectId"].wiki["change-sets"][":id"];
  const param = { projectId: project.id, id };
  const headers = { [CSRF_HEADER]: csrfToken };
  const action = useMutation({
    mutationFn: async (kind: "approve" | "reject" | "submit" | "discard") => {
      if (kind === "submit") {
        await ok(await endpoint.submit.$post({ param }, { headers }));
        return;
      }
      if (kind === "discard") {
        return noContent(await endpoint.discard.$post({ param }, { headers }));
      }
      return noContent(
        await endpoint.items[":itemId"][kind].$post(
          { param: { ...param, itemId: selected?.id ?? "" } },
          { headers },
        ),
      );
    },
    onSuccess: (_, kind) => {
      if (kind === "discard") {
        setDialog(null);
      }
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: wikiKeys.all(project.id) }),
  });
  if (set.isError) {
    return (
      <div className="flex flex-col gap-3 p-4">
        <h2 ref={headingRef} tabIndex={-1} className="sr-only">
          {t("wiki.changes.notFound")}
        </h2>
        <Notice tone="danger">{set.error.message}</Notice>
      </div>
    );
  }
  if (!set.data) {
    return <Skeleton className="m-4 h-48" />;
  }
  const value = set.data;
  const closed = value.status === "published" || value.status === "discarded";
  const working = value.status === "open" && value.submittedAt === null;
  const mine = value.createdBy.toLowerCase() === userId.toLowerCase();
  // The creator previews their own agent set (wiki-collaboration-ux D1): author actions only.
  if (authorPreviewOf(value, userId)) {
    return (
      <AuthorPreview
        project={project}
        set={value}
        selected={itemId}
        onSelect={(next) => onNavigate({ kind: "changes", changeSetId: id, itemId: next })}
        view={view}
        onView={setView}
        // Send hands the message to the thread beside the panel only when it is the set's own
        // conversation; otherwise it opens that conversation first.
        inConversation={
          !!value.provenance.conversation &&
          window.location.pathname.endsWith(`/c/${value.provenance.conversation.id}`)
        }
        headingRef={headingRef}
        runId={runId}
        onRun={(run) => onNavigate({ kind: "changes", changeSetId: id, itemId, runId: run })}
      />
    );
  }
  // Someone else's working set is read only until its author submits it (D3).
  const othersWorking = working && !mine;
  const canWrite = canChange(project, "wiki.draft") && !closed && !othersWorking;
  const canPublish =
    (value.mayPublish ?? canChange(project, "wiki.publish")) && !closed && !othersWorking;
  const stale = value.items.filter((i) => i.status === "stale");
  // Change requests and the trust level (wiki-collaboration D9, D14), as in the review.
  const changeRequests = changeRequestState(value);
  const publishBlocked = changeRequests.publishBlocked;
  const trust = trustState(value);
  const decides = trust.whole;
  const title = (value.hiddenItems ? "" : value.title) || t("wiki.changes.hiddenTitle");
  const counts = [
    t("wiki.card.pages", { count: value.items.filter((i) => !isFolderOp(i.op)).length }),
    ...(value.items.some((i) => isFolderOp(i.op))
      ? [t("wiki.card.folders", { count: value.items.filter((i) => isFolderOp(i.op)).length })]
      : []),
  ].join(", ");
  return (
    <div className="flex flex-col">
      <header className="flex flex-col gap-2 border-b border-line px-4 py-4 sm:px-5">
        <h2
          ref={headingRef}
          tabIndex={-1}
          className="text-lg font-semibold tracking-tight break-words text-fg outline-none"
        >
          {title}
        </h2>
        <p className="text-xs text-fg-secondary tabular-nums">
          {t(`wiki.changes.state.${working ? "working" : value.status}`)} · {counts}
        </p>
        {value.rationale && !value.hiddenItems ? (
          <p className="text-sm break-words text-fg-secondary">{value.rationale}</p>
        ) : null}
        {working ? (
          <Notice tone="info">
            {mine ? t("wiki.changes.working") : t("wiki.changes.workingOther")}
          </Notice>
        ) : null}
        {value.hiddenItems ? <Notice tone="warning">{t("wiki.changes.hidden")}</Notice> : null}
        {canWrite ? (
          <div className="flex flex-wrap gap-1.5">
            {working && mine ? (
              <Button size="sm" disabled={action.isPending} onClick={() => action.mutate("submit")}>
                <Send aria-hidden="true" strokeWidth={1.75} />
                {t("wiki.changes.submit")}
              </Button>
            ) : null}
            {canPublish && decides ? (
              <Button
                size="sm"
                variant={working && mine ? "outline" : "default"}
                disabled={
                  value.hiddenItems ||
                  stale.length > 0 ||
                  publishBlocked ||
                  !value.items.some((i) => i.status === "approved" || i.status === "pending")
                }
                onClick={() => setDialog("publish")}
              >
                {t("wiki.draft.publish")}
              </Button>
            ) : null}
            <Button
              size="sm"
              variant="ghost"
              className="text-fg-secondary"
              disabled={action.isPending}
              onClick={() => setDialog("discard")}
            >
              {t("wiki.review.discard")}
            </Button>
          </div>
        ) : null}
        {canWrite && value.trust && !trust.decides && !working ? (
          <p className="text-xs text-fg-secondary">
            {t(`wiki.changes.trustRefused.${value.trust.level}`)}
          </p>
        ) : null}
        {canWrite && trust.ownEdits && !working ? (
          <p className="text-xs text-fg-secondary">{t("wiki.changes.trustOwnEdits")}</p>
        ) : null}
        {canWrite && canPublish && decides && publishBlocked ? (
          <p className="text-xs text-fg-secondary">{t("wiki.changes.publishBlocked")}</p>
        ) : null}
        {action.isError ? <Notice tone="danger">{action.error.message}</Notice> : null}
      </header>
      <nav aria-label={t("wiki.panel.items")} className="border-b border-line">
        <ul className="divide-y divide-line">
          {value.items.map((entry) => (
            <li key={entry.id}>
              <button
                type="button"
                aria-current={entry.id === selected?.id ? "true" : undefined}
                className={cn(
                  "flex w-full items-start gap-2 px-4 py-2.5 text-left hover:bg-surface-hover sm:px-5",
                  entry.id === selected?.id && "bg-surface-selected",
                )}
                onClick={() => onNavigate({ kind: "changes", changeSetId: id, itemId: entry.id })}
              >
                {isFolderOp(entry.op) ? (
                  <Folder
                    aria-hidden="true"
                    className="mt-0.5 size-4 shrink-0 text-fg-muted"
                    strokeWidth={1.75}
                  />
                ) : (
                  <FileText
                    aria-hidden="true"
                    className="mt-0.5 size-4 shrink-0 text-fg-muted"
                    strokeWidth={1.75}
                  />
                )}
                <span className="flex min-w-0 flex-col gap-0.5">
                  <span className="text-sm font-medium break-words text-fg">{itemName(entry)}</span>
                  <span className="text-xs text-fg-secondary">
                    {itemFacts(entry, working)}
                    {changeRequests.onItem(entry.id) ? (
                      <span className="text-warning-fg">
                        {" · "}
                        {t("wiki.changes.changesRequested")}
                      </span>
                    ) : null}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      </nav>
      {selected ? (
        <section aria-label={itemName(selected)} className="flex flex-col gap-4 px-4 py-4 sm:px-5">
          {item.isError ? (
            <Notice tone="danger">{item.error.message}</Notice>
          ) : !item.data ? (
            <Skeleton className="h-40" />
          ) : (
            <>
              {item.data.staleReason ? (
                <Notice tone="warning">
                  {isFolderOp(item.data.op)
                    ? t("wiki.changes.folderMissing")
                    : t(`wiki.changes.stale.${item.data.staleReason}`)}
                </Notice>
              ) : null}
              <ItemContent projectId={project.id} item={item.data} />
              {canWrite && (item.data.status === "pending" || item.data.status === "approved") ? (
                <div className="flex flex-wrap gap-1.5 border-t border-line pt-3">
                  {item.data.status === "pending" && trust.item(item.data.id) ? (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={action.isPending || changeRequests.onItem(item.data.id)}
                      onClick={() => action.mutate("approve")}
                    >
                      <Check aria-hidden="true" strokeWidth={1.75} />
                      {t("wiki.changes.approve")}
                    </Button>
                  ) : null}
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-fg-secondary"
                    disabled={action.isPending}
                    onClick={() => action.mutate("reject")}
                  >
                    {t("wiki.changes.reject")}
                  </Button>
                </div>
              ) : null}
              {canWrite && changeRequests.onItem(item.data.id) && item.data.status === "pending" ? (
                <p className="text-xs text-fg-secondary">{t("wiki.changes.approveBlocked")}</p>
              ) : null}
              {/* The selected item's thread under its diff (D8a, slice B). */}
              <ThreadPanel
                projectId={project.id}
                set={value}
                itemId={item.data.id}
                canComment={canChange(project, "wiki.view_drafts") && !closed}
              />
            </>
          )}
        </section>
      ) : null}
      <AppDialog
        open={dialog === "discard"}
        onClose={() => setDialog(null)}
        title={t("wiki.card.discardTitle")}
      >
        <p className="text-sm text-fg-secondary">{t("wiki.card.discardBody")}</p>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => setDialog(null)}>
            {t("wiki.editor.cancel")}
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={action.isPending}
            onClick={() => action.mutate("discard")}
          >
            {t("wiki.review.discard")}
          </Button>
        </DialogFooter>
      </AppDialog>
      <ChangeSetPublishDialog
        open={dialog === "publish"}
        onClose={() => setDialog(null)}
        project={project}
        changeSet={value}
      />
    </div>
  );
}
